import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette } from '../components/ui';
import { dateHeureFr } from '../lib/format';
import { T } from '../i18n';

interface U {
  _id: string; nom: string; email: string; telephone?: string;
  role: 'gerant_finances' | 'gerant' | 'agent'; actif: boolean;
  capacitesAccordees: string[]; capacitesRetirees: string[]; dernierAcces?: string;
}
interface Matrice { roles: { code: string; libelle: string; capacites: string[] }[]; capacites: string[] }

const FAMILLE = (c: string) => c.split('.')[0] ?? '';
const LIBELLE = (): Record<string, string> => T.utilisateurs.familles;

/**
 * Le nom francais de chaque droit. La cle ('creer.mouvementCaisse') est ce que
 * le serveur verifie et ne doit jamais bouger ; c'est cette colonne-la que
 * Hichem lit pour decider qui a le droit de quoi. Sans elle, l'ecran demandait
 * a un non-informaticien de lire des identifiants de code — et la note en bas
 * de page parlait de « modifier une donnee financiere », un libelle qui
 * n'apparaissait nulle part dans le tableau.
 * scripts/verifier-langue.mjs verifie qu'aucune capacite n'est sans libelle.
 */
const LIBELLE_CAPACITE = (): Record<string, string> => T.utilisateurs.capacites;

/** « Les droits se reglent dans un ecran, ligne par ligne, sans developpeur. » */
export default function Utilisateurs() {
  const { peut, user } = useAuth();
  const [liste, setListe] = useState<U[] | null>(null);
  const [m, setM] = useState<Matrice | null>(null);
  const [edit, setEdit] = useState<U | null>(null);
  const [nouveau, setNouveau] = useState(false);
  const [f, setF] = useState({ nom: '', email: '', motDePasse: '', role: 'agent', telephone: '' });
  const [erreur, setErreur] = useState('');
  const [msg, setMsg] = useState('');

  const charger = useCallback(() => { api.get<U[]>('/utilisateurs').then(setListe).catch(() => setListe([])); }, []);
  useEffect(() => { charger(); api.get<Matrice>('/utilisateurs/matrice').then(setM); }, [charger]);

  if (!liste || !m) return <Squelette lignes={6} />;
  const admin = peut('gerer.utilisateurs');

  const aLaCapacite = (u: U, c: string) => {
    const base = m.roles.find((r) => r.code === u.role)?.capacites.includes(c) ?? false;
    if (u.capacitesRetirees.includes(c)) return false;
    if (u.capacitesAccordees.includes(c)) return true;
    return base;
  };

  async function basculer(u: U, c: string) {
    const base = m!.roles.find((r) => r.code === u.role)?.capacites.includes(c) ?? false;
    const actuel = aLaCapacite(u, c);
    const cible = !actuel;
    let accordees = u.capacitesAccordees.filter((x) => x !== c);
    let retirees = u.capacitesRetirees.filter((x) => x !== c);
    if (cible !== base) (cible ? accordees : retirees).push(c);
    setErreur('');
    try {
      await api.patch(`/utilisateurs/${u._id}`, { capacitesAccordees: accordees, capacitesRetirees: retirees });
      setMsg(T.utilisateurs.droitChange(c, cible, u.nom));
      charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.utilisateurs.erreur); }
  }

  async function creer() {
    setErreur('');
    try { await api.post('/utilisateurs', f); setNouveau(false); setF({ nom: '', email: '', motDePasse: '', role: 'agent', telephone: '' }); charger(); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.utilisateurs.erreur); }
  }

  const familles = [...new Set(m.capacites.map(FAMILLE))];

  const tu = T.utilisateurs;
  const roleLibelle = (code: string, libelle: string) => T.commun.roles[code] ?? libelle;
  return (
    <>
      <div className="entete">
        <div className="titres"><h1>{tu.titre}</h1><p>{tu.comptes(liste.length)}</p></div>
        {admin && <div className="actions"><button className="btn btn-p" onClick={() => setNouveau(true)}>{tu.ajouter}</button></div>}
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <Carte pad={false}>
        {/* Un tableau doit defiler DANS sa carte : sans cette enveloppe, il pousse
          * la page entiere et, sur telephone, le titre sort de l'ecran. */}
        <div className="enveloppe-tbl">
        <table className="tbl">
          <thead><tr><th>{tu.col.nom}</th><th>{tu.col.email}</th><th>{tu.col.role}</th><th>{tu.col.dernierAcces}</th><th>{tu.col.etat}</th><th /></tr></thead>
          <tbody>
            {liste.map((u) => (
              <tr key={u._id}>
                <td><b>{u.nom}</b>{u._id === user?.id && <span className="mini sourdine">{tu.vous}</span>}</td>
                <td className="mini">{u.email}</td>
                <td>{roleLibelle(u.role, m.roles.find((r) => r.code === u.role)?.libelle ?? u.role)}</td>
                <td className="mini sourdine">{u.dernierAcces ? dateHeureFr(u.dernierAcces) : tu.jamais}</td>
                <td><Badge statut={u.actif ? 'en_cours' : 'annulee'} libelle={u.actif ? tu.actif : tu.desactive} /></td>
                <td className="droite">{admin && <button className="btn btn-s" onClick={() => setEdit(u)}>{tu.droits}</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte titre={tu.matrice} pad={false}>
          <div className="enveloppe-tbl">
            <table className="tbl compact">
              <thead><tr><th>{tu.capacite}</th>{m.roles.map((r) => <th key={r.code} className="droite">{roleLibelle(r.code, r.libelle)}</th>)}</tr></thead>
              <tbody>
                {familles.map((fam) => (
                  <>
                    <tr key={`f-${fam}`}><td colSpan={4} style={{ background: 'var(--zebre)' }} className="micro">{LIBELLE()[fam] ?? fam}</td></tr>
                    {m.capacites.filter((c) => FAMILLE(c) === fam).map((c) => (
                      <tr key={c}>
                        <td className="mini">{LIBELLE_CAPACITE()[c] ?? c}<div className="micro sourdine">{c}</div></td>
                        {m.roles.map((r) => (
                          <td key={r.code} className="droite" style={{ color: r.capacites.includes(c) ? 'var(--succes)' : 'var(--bord-fort)', fontWeight: 700 }}>
                            {r.capacites.includes(c) ? '✓' : '·'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </Carte>
      </div>

      <p className="mini sourdine" style={{ marginTop: 12, maxWidth: 780 }}>
        {tu.note}
      </p>

      {edit && (
        <Modale titre={tu.droitsDe(edit.nom)} onFermer={() => setEdit(null)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            {tu.aideCoche}
          </p>
          <div style={{ display: 'grid', gap: 4, maxHeight: '55vh', overflowY: 'auto' }}>
            {m.capacites.map((c) => {
              const base = m.roles.find((r) => r.code === edit.role)?.capacites.includes(c) ?? false;
              const actuel = aLaCapacite(edit, c);
              return (
                <label key={c} style={{
                  display: 'flex', gap: 9, alignItems: 'center', padding: '7px 10px',
                  border: '1px solid var(--bord)', borderRadius: 'var(--r-champ)',
                  background: actuel !== base ? 'var(--accent-doux)' : 'var(--surface)', cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={actuel} onChange={() => void basculer(edit, c)} />
                  <span style={{ flex: 1 }}>{c}</span>
                  {actuel !== base && <span className="mini" style={{ color: 'var(--accent-fonce)', fontWeight: 700 }}>{tu.exception}</span>}
                </label>
              );
            })}
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => { setEdit(null); charger(); }}>{tu.fermer}</button>
          </div>
        </Modale>
      )}

      {nouveau && (
        <Modale titre={tu.nouvel} onFermer={() => setNouveau(false)}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Champ label={tu.nom}><input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} /></Champ>
            <Champ label={tu.email}><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Champ>
            <Champ label={tu.telephone}><input value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} /></Champ>
            <Champ label={tu.role}>
              <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {m.roles.map((r) => <option key={r.code} value={r.code}>{roleLibelle(r.code, r.libelle)}</option>)}
              </select>
            </Champ>
            <Champ label={tu.motDePasse} aide={tu.motDePasseAide}><input type="password" value={f.motDePasse} onChange={(e) => setF({ ...f, motDePasse: e.target.value })} /></Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setNouveau(false)}>{tu.annuler}</button>
            <button className="btn btn-p" disabled={!f.nom || !f.email || f.motDePasse.length < 6} onClick={() => void creer()}>{tu.creer}</button>
          </div>
        </Modale>
      )}
    </>
  );
}
